import { getRequest } from '../Util/utils';
import { postComposerPayload, simpleResponse } from './composerRequest';

export type MessagingUser = {
  id: number;
  username: string;
  first_name: string;
  last_name: string;
  profile_photo?: string;
};

export type FileAttachment = {
  type: 'image' | 'video' | 'audio' | 'file';
  url?: string;
  mime?: string;
  size?: number;
  // Original filename -- 'file' attachments need it to show/download as something meaningful
  // (the stored path is just a UUID); image/video/audio don't currently render it anywhere.
  name?: string;
};

// A reference to one of the sender's own activities/workouts, not an uploaded file -- the full
// card data is fetched fresh by path whenever the message is rendered (see AttachedExerciseCard).
export type ExerciseAttachment = {
  type: 'activity' | 'workout';
  path: string;
};

export type MessageAttachment = FileAttachment | ExerciseAttachment;

export type ExerciseReference = { type: 'activity' | 'workout'; path: string };

export type ReplyPreview = { id: string; body: string | null; sender_id: number; deleted: boolean };

export type ChatMessage = {
  id: string;
  conversation_id: string;
  sender_id: number;
  body: string | null;
  attachments: MessageAttachment[];
  created_at: string;
  read_by: { user_id: number; at: string }[];
  edited_at?: string | null;
  deleted_at?: string | null;
  reactions: { user_id: number; emoji: string }[];
  pinned_at?: string | null;
  reply_to: ReplyPreview | null;
  forwarded_from: MessagingUser | null;
  // True if this message was ever relayed to/from WhatsApp -- either it arrived as an inbound WA
  // reply, or it was forwarded outbound to at least one recipient's linked WhatsApp
  // (BaseMessageService::viaWhatsApp()). Drives the small WA badge on the bubble.
  via_whatsapp: boolean;
};

export type Conversation = {
  id: string;
  type: 'dm' | 'group';
  club_id?: string | null;
  club_ids?: string[];
  name?: string | null;
  participants: MessagingUser[];
  last_message_at?: string | null;
  last_message_preview?: string | null;
  unread_count?: number;
};

export async function getConversations(): Promise<Conversation[]> {
  return window
    .fetch('/messaging/conversations', getRequest())
    .then((r) => simpleResponse<{ conversations: Conversation[] }>(r))
    .then((data) => data.conversations);
}

// Who the current user can start a new conversation with -- a coach gets every active athlete
// they coach, an athlete gets just their assigned coach (or the club's head coach).
export async function getContacts(): Promise<MessagingUser[]> {
  return window
    .fetch('/messaging/contacts', getRequest())
    .then((r) => simpleResponse<{ contacts: MessagingUser[] }>(r))
    .then((data) => data.contacts);
}

export async function startConversationWith(userId: number): Promise<Conversation> {
  return window
    .fetch('/messaging/conversations', {
      ...getRequest(),
      method: 'POST',
      body: JSON.stringify({ type: 'dm', user_id: userId }),
    })
    .then((r) => simpleResponse<{ conversation: Conversation }>(r))
    .then((data) => data.conversation);
}

export async function getMessages(conversationId: string, before?: string): Promise<ChatMessage[]> {
  const params = before ? '?' + new URLSearchParams({ before }) : '';
  return window
    .fetch(`/messaging/conversations/${conversationId}/messages` + params, getRequest())
    .then((r) => simpleResponse<{ messages: ChatMessage[] }>(r))
    .then((data) => data.messages);
}

// Sends body text plus any staged files/exercise references as a single message. See
// composerRequest.ts::postComposerPayload for the JSON-vs-multipart transport logic.
export async function sendMessage(conversationId: string, body: string, files: File[] = [], references: ExerciseReference[] = [], replyToId?: string): Promise<ChatMessage> {
  return postComposerPayload<{ message: ChatMessage }>(`/messaging/conversations/${conversationId}/messages`, { body, files, references, replyToId }).then((data) => data.message);
}

export async function markConversationRead(conversationId: string): Promise<void> {
  await window.fetch(`/messaging/conversations/${conversationId}/read`, {
    ...getRequest(),
    method: 'POST',
  });
}

export async function editMessage(messageId: string, body: string): Promise<ChatMessage> {
  return window
    .fetch(`/messaging/messages/${messageId}`, { ...getRequest(), method: 'PUT', body: JSON.stringify({ body }) })
    .then((r) => simpleResponse<{ message: ChatMessage }>(r))
    .then((data) => data.message);
}

export async function deleteMessage(messageId: string): Promise<void> {
  await window.fetch(`/messaging/messages/${messageId}`, { ...getRequest(), method: 'DELETE' });
}

export async function toggleReaction(messageId: string, emoji: string): Promise<ChatMessage> {
  return window
    .fetch(`/messaging/messages/${messageId}/reactions`, { ...getRequest(), method: 'POST', body: JSON.stringify({ emoji }) })
    .then((r) => simpleResponse<{ message: ChatMessage }>(r))
    .then((data) => data.message);
}

export async function pinMessage(messageId: string): Promise<ChatMessage> {
  return window
    .fetch(`/messaging/messages/${messageId}/pin`, { ...getRequest(), method: 'POST' })
    .then((r) => simpleResponse<{ message: ChatMessage }>(r))
    .then((data) => data.message);
}

export async function unpinMessage(messageId: string): Promise<ChatMessage> {
  return window
    .fetch(`/messaging/messages/${messageId}/pin`, { ...getRequest(), method: 'DELETE' })
    .then((r) => simpleResponse<{ message: ChatMessage }>(r))
    .then((data) => data.message);
}

export async function forwardMessage(messageId: string, conversationId: string): Promise<ChatMessage> {
  return window
    .fetch(`/messaging/messages/${messageId}/forward`, { ...getRequest(), method: 'POST', body: JSON.stringify({ conversation_id: conversationId }) })
    .then((r) => simpleResponse<{ message: ChatMessage }>(r))
    .then((data) => data.message);
}
